
import { useContext } from "react";
import { AuthContext } from "./AuthContext";
import { forceRedirect } from "@/integrations/supabase/client";

// Cache for emergency fallback profile data - shared across hook instances
const emergencyCache = {
  profileErrorToastShown: false,
  lastProfileCheck: 0,
  errorMessageShown: false
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // If context is undefined, log a detailed error but provide a fallback instead of throwing
  if (context === undefined) {
    console.error("useAuth was used outside of AuthProvider! This is a critical error.");
    
    // Try to get any cached data from localStorage for emergency fallback
    let cachedProfile = null;
    let cachedOrganization = null;
    
    try {
      const profileId = localStorage.getItem('lastProfileId');
      const profileRole = localStorage.getItem('lastProfileRole');
      const orgId = localStorage.getItem('lastOrganizationId');
      const orgName = localStorage.getItem('lastOrganizationName');
      const firstName = localStorage.getItem('lastProfileFirstName');
      const lastName = localStorage.getItem('lastProfileLastName');
      
      if (profileId) {
        cachedProfile = {
          id: profileId,
          role: profileRole || 'member',
          organization_id: orgId || null,
          first_name: firstName || '',
          last_name: lastName || '',
          avatar_url: null,
          email_notifications: false
        };
      }
      
      if (orgId) {
        cachedOrganization = {
          id: orgId,
          name: orgName || 'Organization'
        };
      }
      
      console.log("Using emergency cached profile data:", { 
        hasProfile: !!cachedProfile, 
        hasOrg: !!cachedOrganization 
      });
    } catch (e) {
      console.error("Failed to retrieve cache fallback data:", e);
    }
    
    // If we're on the auth page, try to redirect to login
    if (window.location.pathname !== '/auth') {
      console.log("Redirecting to auth page due to missing AuthContext");
      setTimeout(() => {
        // Get the current path to use as return URL
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        forceRedirect(`/auth?returnTo=${returnTo}`);
      }, 100);
    }
    
    // Return a fallback object that won't crash the app
    return {
      session: null,
      user: null, 
      isLoading: false,
      loading: false,
      signOut: async () => { console.error("Auth context missing"); },
      organization: cachedOrganization,
      profile: cachedProfile,
      profileLoaded: true
    };
  }
  
  // Only log detailed debug info if specified in environment or localStorage
  const shouldLogDebug = localStorage.getItem('enableAuthDebug') === 'true';
  
  if (shouldLogDebug) {
    console.log("Auth context used:", { 
      hasUser: !!context.user, 
      isLoading: context.isLoading,
      profileLoaded: context.profileLoaded,
      hasProfile: !!context.profile,
      hasOrganization: !!context.organization
    });
  }
  
  // Create a wrapped version that catches profile errors and provides resilience
  const safeContext = {
    ...context,
    // Ensure profile is never undefined
    profile: context.profile || null,
    // Ensure organization is never undefined
    organization: context.organization || null,
    // Ensure these flags are boolean
    profileLoaded: !!context.profileLoaded,
    isLoading: !!context.isLoading,
    loading: !!context.isLoading
  };
  
  // Reset error flags when profile loads successfully
  if (safeContext.profile && emergencyCache.profileErrorToastShown) {
    emergencyCache.profileErrorToastShown = false;
    emergencyCache.errorMessageShown = false;
  }
  
  return safeContext;
};
